// [목적] AI 자연어 보정(refine, C5) 상태 훅 — propose(제안)→apply(적용) 흐름 + ApiError 사용자 메시지 매핑
// [이유] FlowLine dcc2c3cc FE 과업1 — FolioEditPage(데스크톱)/FolioMobile(모바일) 양쪽이 동일 로직을
//        재사용(에러 매핑 중복 방지, ReportDialog.tsx 의 mapReportError 관례를 훅 레벨로 승격).
// [사이드 이팩트] apply 성공 시 queryKeys.folio(folioId) invalidate(뷰 자동 갱신) + useToast 로 성공 알림.
//                dialog 의 open 상태를 이 훅이 직접 소유 — propose() 호출 시 open=true, apply 성공/
//                cancel() 시 open=false 로 전환되어 호출부는 별도 open state 를 관리하지 않아도 된다.
// [수정 시 고려사항] BE 가 rag.* 에러 코드를 추가하면 mapRefineError 에 케이스 추가.
//                   applyMutation 은 proposeMutation.data(제안)의 proposalId 를 참조 — propose 없이
//                   apply() 를 호출하면 즉시 reject(방어적, UI 는 proposal 존재 시에만 적용 버튼 노출해야 함).

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RefineProposalResponse, RefineScope } from '@gangwoncanvas/types';

import { proposeRefine, applyRefineProposal } from '../api/refine.client.js';
import { queryKeys } from '../api/queryKeys.js';
import { ApiError } from '../api/http.js';
import { useToast } from '../components/feedback/Toast.js';

/**
 * [목적] ApiError → 사용자 안내 문구 매핑 (rag.* 도메인 코드 + folio.NOT_FOUND + 429 레이트리밋)
 * [이유] BE 계약(codes.ts) 의 dot-namespaced 코드를 화면별로 중복 분기하지 않도록 단일 함수로 고정.
 *        만료 케이스 문구는 작업 카드 명시 문구("제안이 만료되었어요, 다시 요청해 주세요")를 그대로 사용.
 * [사이드 이팩트] 없음(순수 함수) — 단위 테스트로 export 하여 직접 검증.
 * [수정 시 고려사항] BE 가 코드를 추가/변경하면 이 함수만 갱신하면 두 화면에 동시 반영됨.
 */
export function mapRefineError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'rag.REFINE_PROPOSAL_NOT_FOUND') {
      return '제안이 만료되었어요, 다시 요청해 주세요.';
    }
    if (err.code === 'rag.INPUT_BLOCKED') {
      return '입력하신 내용에 부적절한 표현이 포함되어 있어요. 다시 작성해 주세요.';
    }
    if (err.code === 'rag.REFINE_SCOPE_INVALID') {
      return '요청한 일정을 찾을 수 없어요. 새로고침 후 다시 시도해 주세요.';
    }
    if (err.code === 'rag.REFINE_GENERATION_FAILED') {
      return 'AI 제안을 만드는 데 실패했어요. 잠시 후 다시 시도해 주세요.';
    }
    if (err.code === 'folio.NOT_FOUND') {
      return '폴리오를 찾을 수 없어요.';
    }
    if (err.status === 429) {
      return '요청이 너무 많아요. 잠시 후 다시 시도해 주세요.';
    }
  }
  return '요청 처리 중 오류가 발생했어요. 잠시 후 다시 시도해 주세요.';
}

/** 제안이 기존 Day 와 실제로 다를 때 — 안내 문구(토스트). 표현을 바꿔 다시 시도하도록 유도한다 */
export const REFINE_NO_CHANGE_MESSAGE =
  '요청하신 내용을 반영할 수 있는 장소를 찾지 못했어요. 표현을 바꿔 다시 요청해 보세요.';

/**
 * [목적] 제안(after)이 기존 Day(before)와 실제로 다른지 판정 — 항목 구성 또는 순서가 하나라도 다르면 true
 * [이유] QA P2-03: LLM 이 후보에 없는 항목을 넣으면 BE 가 조용히 걸러 after 가 before 와 같아진다. 그런데도
 *        미리보기를 띄우고 [적용]을 누르게 하면 "적용했어요"만 뜨고 화면은 그대로여서 무응답처럼 보였다.
 *        BE 도 이 경우 changes 를 비우지만, 구버전 응답·다른 경로에 대비해 FE 가 스냅샷으로 한 번 더 판정한다.
 * [사이드 이팩트] 없음(순수 함수). contentId 순서열만 비교 — BE hasProposalChanges 와 같은 기준.
 * [수정 시 고려사항] 항목 식별 기준을 바꾸면 BE folio-refine.service.ts hasProposalChanges 도 함께 갱신할 것.
 */
export function hasActualChanges(proposal: RefineProposalResponse): boolean {
  const before = proposal.day.before.items.map((it) => it.citation.contentId);
  const after = proposal.day.after.items.map((it) => it.citation.contentId);
  return before.length !== after.length || before.some((id, idx) => id !== after[idx]);
}

export interface UseFolioRefineResult {
  /** 미리보기 다이얼로그 표시 여부 — propose() 호출 시 true, cancel()/적용 성공 시 false */
  open: boolean;
  propose: (instruction: string, scope: RefineScope) => void;
  proposal: RefineProposalResponse | null;
  isProposing: boolean;
  proposeErrorMessage: string | null;
  apply: () => void;
  isApplying: boolean;
  applyErrorMessage: string | null;
  /** 다이얼로그 닫기 + 제안/에러 상태 초기화 (취소 버튼) */
  cancel: () => void;
  /**
   * 적용에 성공할 때마다 1씩 증가 — 호출부가 RefineInputBar 의 key 로 넘기면 입력창이 비워진다.
   * (입력창은 자체 상태를 가져 부모가 직접 지울 수 없다. 변경 없음/실패 시에는 그대로 두어 수정해 재요청할 수 있다.)
   */
  resetKey: number;
}

/**
 * [목적] 폴리오 Day 단위 AI 보정 propose→apply 흐름 훅
 * [이유] FlowLine dcc2c3cc — RefineInputBar 제출 → propose() → RefineProposalDialog 미리보기 →
 *        apply() → 폴리오 쿼리 무효화 + 토스트. 두 mutation 을 하나의 훅으로 캡슐화해 화면 코드를 단순화.
 * [사이드 이팩트] useToast 컨텍스트 의존(ToastProvider 하위에서만 사용 가능 — App 루트 마운트 보장됨).
 * [수정 시 고려사항] 동시에 여러 Day 를 보정 대상으로 열 수 없음(단일 open 상태) — 필요 시 폴리오당
 *                   훅 인스턴스를 여러 개 두지 않고 scope 파라미터로 대상만 바꿔 재사용할 것.
 */
export function useFolioRefine(folioId: string): UseFolioRefineResult {
  const qc = useQueryClient();
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const proposeMutation = useMutation({
    mutationFn: (vars: { instruction: string; scope: RefineScope }) =>
      proposeRefine(folioId, vars.instruction, vars.scope),
    onSuccess: (proposal) => {
      // [QA-P2-03] 실제로 바뀌는 게 없으면 빈 미리보기를 띄우지 않고 닫은 뒤 이유를 알린다(입력은 그대로 유지)
      if (!hasActualChanges(proposal)) {
        setOpen(false);
        show(REFINE_NO_CHANGE_MESSAGE, 'info');
      }
    },
  });

  const applyMutation = useMutation({
    mutationFn: () => {
      const proposalId = proposeMutation.data?.proposalId;
      if (!proposalId) return Promise.reject(new Error('적용할 제안이 없어요.'));
      return applyRefineProposal(folioId, proposalId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.folio(folioId) });
      show('AI 보정을 적용했어요.', 'success');
      setOpen(false);
      // [QA-P2-03] 적용까지 끝났으니 입력창을 비운다(호출부가 key={resetKey} 로 RefineInputBar 를 재마운트)
      setResetKey((k) => k + 1);
    },
  });

  function propose(instruction: string, scope: RefineScope): void {
    setOpen(true);
    proposeMutation.mutate({ instruction, scope });
  }

  function cancel(): void {
    setOpen(false);
    proposeMutation.reset();
    applyMutation.reset();
  }

  return {
    open,
    propose,
    proposal: proposeMutation.data ?? null,
    isProposing: proposeMutation.isPending,
    proposeErrorMessage: proposeMutation.error ? mapRefineError(proposeMutation.error) : null,
    apply: () => applyMutation.mutate(),
    isApplying: applyMutation.isPending,
    applyErrorMessage: applyMutation.error ? mapRefineError(applyMutation.error) : null,
    cancel,
    resetKey,
  };
}
